import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Skeleton } from "../components/Loader";
import DatePicker from "react-datepicker";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  seriesColor,
  STATUS_COLORS,
} from "../shared/chartTheme";

import {
  fetchAccountingReport,
  selectAccountingData,
  selectAccountingError,
  selectAccountingStatus,
} from "../features/reports/reportsSlice";
import { fetchStores, selectStores, selectStoresStatus } from "../features/stores/storesSlice";
import StoreMultiSelect from "../components/StoreMultiSelect";

import "./ReportPage.css";

/**
 * Обзор бухгалтерии — замена дашборда Metabase `7-bugalteriya-auf` (AUFB-65).
 *
 * Две несвязанные части, как и на дашборде: прибыль от продаж за период и оценка
 * склада в трёх ценах (розничная, себестоимость, оптовая). Оценка склада —
 * срез «сейчас», от периода не зависит; на странице это подписано.
 */

const CHART_COLORS = {
  profit: STATUS_COLORS.success,
  retail: seriesColor(0),
  selfCost: seriesColor(1),
  wholesale: seriesColor(2),
  quantity: seriesColor(3),
};

function formatMoney(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("ru-RU", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Крупные суммы в KPI — без копеек: 201 872 073.00 не читается. */
function formatMoneyShort(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("ru-RU", { maximumFractionDigits: 0 });
}

function formatCount(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return num.toLocaleString("ru-RU");
}

function formatQuantity(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0";
  return Number.isInteger(num) ? num.toLocaleString("ru-RU") : num.toLocaleString("ru-RU", { maximumFractionDigits: 3 });
}

function formatPct(value) {
  const num = Number(value || 0);
  if (Number.isNaN(num)) return "0.00%";
  return `${num.toFixed(2)}%`;
}

function formatAxisMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 10_000) return `${(n / 1000).toFixed(0)}k`;
  return String(Math.round(n));
}

function formatWeek(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" });
}

function formatWeekFull(v) {
  if (!v) return "";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return `Неделя с ${d.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" })}`;
}

function toIsoLocal(d) {
  if (!d) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseIsoLocal(iso) {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d);
}

function todayIso() {
  return toIsoLocal(new Date());
}

function KpiCard({ title, value, hint, accent }) {
  return (
    <div className="kpi-card">
      <div className="kpi-title">{title}</div>
      <div className={accent ? `kpi-value ${accent}` : "kpi-value"}>{value}</div>
      {hint && <div className="kpi-hint">{hint}</div>}
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div className="section">
      <div className="section-head">
        <div className="section-title">{title}</div>
        {subtitle && <div className="section-subtitle">{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function Empty({ text = "Нет данных" }) {
  return <div className="empty">{text}</div>;
}

function BreakdownTable({ rows, columns, emptyText, footer }) {
  if (!rows || rows.length === 0) return <Empty text={emptyText} />;
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            {columns.map((col) => (
              <th key={col.key} className={col.numeric ? "numeric" : undefined}>
                {col.title}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.id ?? index}>
              {columns.map((col) => {
                const raw = row[col.key];
                let content = raw;
                if (col.money) content = formatMoney(raw);
                else if (col.pct) content = formatPct(raw);
                else if (col.quantity) content = formatQuantity(raw);
                else if (col.numeric) content = formatCount(raw);
                else if (raw === null || raw === undefined || raw === "") content = "—";
                const cellClasses = [col.numeric ? "numeric" : "", col.bold ? "bold" : ""].filter(Boolean).join(" ");
                return (
                  <td key={col.key} className={cellClasses || undefined}>
                    {content}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {footer && <div className="hint-block">{footer}</div>}
    </div>
  );
}

const tooltipStyle = {
  background: "var(--c-card)",
  border: "1px solid var(--c-border)",
  borderRadius: 8,
  color: "var(--c-text)",
  fontSize: 14,
};

/** Столбцы «цена + количество» на двух осях — три панели дашборда устроены одинаково. */
function PriceQuantityChart({ rows, valueKey, valueName, color }) {
  if (!rows || rows.length === 0) return <Empty />;
  return (
    <ResponsiveContainer width="100%" height={340}>
      <BarChart data={rows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
        <XAxis
          dataKey="store"
          stroke="var(--c-text-muted)"
          fontSize={10}
          interval={0}
          angle={-45}
          textAnchor="end"
          height={90}
        />
        <YAxis yAxisId="left" tickFormatter={formatAxisMoney} stroke="var(--c-text-muted)" fontSize={12} />
        <YAxis
          yAxisId="right"
          orientation="right"
          tickFormatter={formatAxisMoney}
          stroke="var(--c-text-muted)"
          fontSize={12}
        />
        <Tooltip
          contentStyle={tooltipStyle}
          formatter={(value, name) =>
            name === "Количество" ? [formatQuantity(value), name] : [formatMoney(value), name]
          }
        />
        <Legend wrapperStyle={{ fontSize: 14 }} />
        <Bar yAxisId="left" dataKey={valueKey} name={valueName} fill={color} radius={[3, 3, 0, 0]} />
        <Bar
          yAxisId="right"
          dataKey="quantity_num"
          name="Количество"
          fill={CHART_COLORS.quantity}
          radius={[3, 3, 0, 0]}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function AccountingPage() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const stores = useSelector(selectStores);
  const storesStatus = useSelector(selectStoresStatus);

  const data = useSelector(selectAccountingData);
  const status = useSelector(selectAccountingStatus);
  const error = useSelector(selectAccountingError);

  const [storeIds, setStoreIds] = useState(() => {
    const raw = searchParams.getAll("store_ids");
    return raw.length ? raw.map(String) : [];
  });
  const [startDate, setStartDate] = useState(() => searchParams.get("start_date") || todayIso());
  const [endDate, setEndDate] = useState(() => searchParams.get("end_date") || todayIso());

  useEffect(() => {
    if (storesStatus === "idle") dispatch(fetchStores({ pageSize: 200 }));
  }, [dispatch, storesStatus]);

  const load = useCallback(() => {
    const numericIds = storeIds.map((id) => Number(id)).filter((id) => Number.isFinite(id));
    dispatch(fetchAccountingReport({ storeIds: numericIds, startDate, endDate, weeks: 52 }));
  }, [dispatch, storeIds, startDate, endDate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams();
    storeIds.forEach((id) => params.append("store_ids", String(id)));
    if (startDate) params.set("start_date", startDate);
    if (endDate) params.set("end_date", endDate);
    setSearchParams(params, { replace: true });
  }, [storeIds, startDate, endDate, setSearchParams]);

  const handleReset = () => {
    setStoreIds([]);
    setStartDate(todayIso());
    setEndDate(todayIso());
  };

  const profit = data?.profit;
  const stock = data?.stock;

  const profitByStore = useMemo(
    () =>
      (data?.profit_by_store || []).map((row) => ({
        ...row,
        id: row.store_id,
        store: row.store_name,
        profit_num: Number(row.gross_profit || 0),
      })),
    [data]
  );

  const weeklyRows = useMemo(
    () =>
      (data?.weekly_profit || []).map((row) => ({
        ...row,
        profit_num: Number(row.gross_profit || 0),
      })),
    [data]
  );

  const stockRows = useMemo(
    () =>
      (data?.stock_by_store || []).map((row) => ({
        ...row,
        id: row.store_id,
        retail_num: Number(row.retail_value || 0),
        self_cost_num: Number(row.self_cost_value || 0),
        wholesale_num: Number(row.wholesale_value || 0),
        quantity_num: Number(row.quantity || 0),
      })),
    [data]
  );

  return (
    <div className="sales-dashboard-page">
      <div className="header">
        <div>
          <div className="title">Бухгалтерия</div>
          <div className="subtitle">Прибыль за период и оценка склада в трёх ценах</div>
        </div>
      </div>

      <div className="toolbar">
        <StoreMultiSelect stores={stores} value={storeIds} onChange={setStoreIds} />
        <div className="field">
          <label className="label">С даты</label>
          <DatePicker
            selected={parseIsoLocal(startDate)}
            onChange={(d) => setStartDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="дд.мм.гггг"
            customInput={<input className="input" />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
        </div>
        <div className="field">
          <label className="label">По дату</label>
          <DatePicker
            selected={parseIsoLocal(endDate)}
            onChange={(d) => setEndDate(toIsoLocal(d))}
            dateFormat="dd.MM.yyyy"
            placeholderText="дд.мм.гггг"
            customInput={<input className="input" />}
            wrapperClassName="pnl-datepicker-wrapper"
          />
          <div className="hint">Период действует только на прибыль</div>
        </div>
        <div className="toolbar-buttons">
          <button type="button" onClick={load} className="apply-btn">
            Обновить
          </button>
          <button type="button" onClick={handleReset} className="reset-btn">
            Сбросить
          </button>
        </div>
      </div>

      {status === "loading" && <Skeleton variant="cards" count={4} text="Загрузка отчёта…" style={{ padding: "16px 24px 32px" }} />}
      {status === "failed" && error && <div className="error-box">{error}</div>}

      <div className="body">
        <div className="kpi-grid">
          <KpiCard
            title="Прибыль"
            value={formatMoneyShort(profit?.gross_profit)}
            accent="accent"
            hint={`маржа ${formatPct(profit?.margin_pct)}`}
          />
          <KpiCard
            title="Выручка"
            value={formatMoneyShort(profit?.revenue)}
            hint={`${formatCount(profit?.sale_count)} чеков`}
          />
          <KpiCard
            title="Сумма товаров по себестоимости"
            value={formatMoneyShort(stock?.self_cost_value)}
            hint="остаток на складе"
          />
          <KpiCard
            title="Сумма всех товаров по рознице"
            value={formatMoneyShort(stock?.retail_value)}
            hint={`${formatQuantity(stock?.total_quantity)} шт.`}
          />
        </div>

        <Section title="График прибыли" subtitle="По магазинам за период">
          {profitByStore.length === 0 ? (
            <Empty text="Нет продаж за выбранный период" />
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              <BarChart data={profitByStore} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                <XAxis
                  dataKey="store"
                  stroke="var(--c-text-muted)"
                  fontSize={10}
                  interval={0}
                  angle={-45}
                  textAnchor="end"
                  height={90}
                />
                <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-muted)" fontSize={12} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [formatMoney(v), "Прибыль"]} />
                <Bar dataKey="profit_num" name="Прибыль" fill={CHART_COLORS.profit} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section title="График прибыли" subtitle="По неделям, вся история">
          {weeklyRows.length === 0 ? (
            <Empty />
          ) : (
            <ResponsiveContainer width="100%" height={340}>
              <LineChart data={weeklyRows} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--c-border)" />
                <XAxis dataKey="week" tickFormatter={formatWeek} stroke="var(--c-text-muted)" fontSize={11} />
                <YAxis tickFormatter={formatAxisMoney} stroke="var(--c-text-muted)" fontSize={12} />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelFormatter={formatWeekFull}
                  formatter={(v) => [formatMoney(v), "Прибыль"]}
                />
                <Line
                  type="monotone"
                  dataKey="profit_num"
                  name="Прибыль"
                  stroke={CHART_COLORS.profit}
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Section>

        <Section title="По розничной стоимости" subtitle="Остаток склада">
          <PriceQuantityChart
            rows={stockRows}
            valueKey="retail_num"
            valueName="Розничная стоимость"
            color={CHART_COLORS.retail}
          />
        </Section>

        <Section title="По собственной себестоимости" subtitle="Остаток склада">
          <PriceQuantityChart
            rows={stockRows}
            valueKey="self_cost_num"
            valueName="Себестоимость"
            color={CHART_COLORS.selfCost}
          />
        </Section>

        <Section title="По оптовой цене" subtitle="Остаток склада">
          <PriceQuantityChart
            rows={stockRows}
            valueKey="wholesale_num"
            valueName="Оптовая цена"
            color={CHART_COLORS.wholesale}
          />
        </Section>

        <Section title="Сумма всех товаров по магазинам">
          <BreakdownTable
            rows={stockRows}
            columns={[
              { key: "store", title: "Магазин" },
              { key: "retail_value", title: "По розничной цене", numeric: true, money: true, bold: true },
              { key: "self_cost_value", title: "По себестоимости", numeric: true, money: true },
              { key: "wholesale_value", title: "По оптовым ценам", numeric: true, money: true },
              { key: "quantity", title: "Количество", numeric: true, quantity: true },
            ]}
            footer={`${stockRows.length} строк`}
            emptyText="На складе нет товаров"
          />
        </Section>
      </div>
    </div>
  );
}
